import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Paywall from '../components/Paywall';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_META = {
  WOMEN: {
    label: "Women's Library",
    eyebrow: 'For her',
    pillars: [
      { icon: 'fa-shield-halved', title: 'Pelvic Health',     sub: 'Strength · Recovery' },
      { icon: 'fa-spa',           title: 'Hormonal Balance',  sub: 'Cycle · Menopause' },
      { icon: 'fa-star',          title: 'Intimate Confidence', sub: 'Body · Pleasure' },
    ],
  },
  MEN: {
    label: "Men's Library",
    eyebrow: 'For him',
    pillars: [
      { icon: 'fa-bolt',          title: 'Performance',       sub: 'Erection · Stamina' },
      { icon: 'fa-heart-pulse',   title: 'Hormonal & Prostate', sub: 'Testosterone · BPH' },
      { icon: 'fa-dumbbell',      title: 'Wellness Lifestyle', sub: 'Sleep · Strength' },
    ],
  },
};

const CAT_ICONS = {
  'Pelvic Health': 'fa-shield-halved',
  'Menstrual Wellness': 'fa-droplet',
  'Menopause Wellness': 'fa-spa',
  'Postpartum': 'fa-baby',
  'Female Wellness': 'fa-heart',
  'Hormonal Health': 'fa-scale-balanced',
  'Libido Science': 'fa-fire',
  'Relationship Intimacy': 'fa-users',
  'Sexual Confidence': 'fa-star',
  'Intimate Hygiene': 'fa-droplet',
  'Surgical Wellness': 'fa-stethoscope',
  "Men's Sexual Health": 'fa-mars',
  'Erectile & Performance': 'fa-bolt',
  'Prostate & Hormonal': 'fa-heart-pulse',
  'Male Wellness Lifestyle': 'fa-dumbbell',
};

function inferSection(article) {
  if (article.section === 'WOMEN' || article.section === 'MEN') return article.section;
  const c = article.category || '';
  if (c.startsWith("Men's") || c === 'Erectile & Performance' || c === 'Prostate & Hormonal' || c === 'Male Wellness Lifestyle') {
    return 'MEN';
  }
  return 'WOMEN';
}

export default function Education() {
  const { authHeader, isPremium } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [activeSection, setActiveSection] = useState('WOMEN');
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [paywall, setPaywall] = useState(false);
  const [loading, setLoading] = useState(true);

  const openArticle = (a) => navigate(`/article/${a.id}`);

  useEffect(() => {
    axios.get(`${API}/api/education`, { headers: authHeader() })
      .then(r => setArticles(r.data.articles || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Categories of the current section
  const sectionArticles = useMemo(
    () => articles.filter(a => inferSection(a) === activeSection),
    [articles, activeSection]
  );

  const categories = useMemo(() => {
    const cats = ['All', ...Array.from(new Set(sectionArticles.map(a => a.category)))];
    return cats;
  }, [sectionArticles]);

  // When section changes, reset category
  useEffect(() => { setActiveCategory('All'); }, [activeSection]);

  const filtered = useMemo(() => sectionArticles.filter(a =>
    (activeCategory === 'All' || a.category === activeCategory) &&
    (!search || a.title.toLowerCase().includes(search.toLowerCase()) || (a.excerpt || '').toLowerCase().includes(search.toLowerCase()))
  ), [sectionArticles, activeCategory, search]);

  const featured = useMemo(() => sectionArticles.slice(0, 6), [sectionArticles]);

  const meta = SECTION_META[activeSection];

  return (
    <div className={`education-shell theme-${activeSection.toLowerCase()}`} style={{ minHeight: '100vh', paddingBottom: 'var(--bottom-h)', position: 'relative' }}>
      <div className="education-aurora" aria-hidden="true" />
      {/* Calm-inspired hero */}
      <div className="calm-hero">
        <div className="eyebrow" style={{ marginBottom: 8 }}>{meta.eyebrow}</div>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '2.5rem',
          fontWeight: 500,
          lineHeight: 1.18,
          letterSpacing: '-0.01em',
          maxWidth: 360,
          margin: '0 auto 10px',
        }}>
          {activeSection === 'WOMEN'
            ? <>Calm your body. <span className="gold-italic">Reclaim intimacy.</span></>
            : <>Strength inside. <span className="gold-italic">Confidence outside.</span></>}
        </h1>
        <p style={{
          fontFamily: 'var(--font-ui)',
          fontSize: '0.94rem',
          color: 'var(--text-muted)',
          maxWidth: 320,
          margin: '0 auto',
          lineHeight: 1.65,
          fontWeight: 400,
        }}>
          {articles.length}+ expert articles by Dr. Vikram Dukle — gynaecologist-led, judgement-free, in your pocket.
        </p>
      </div>

      {/* Section toggle */}
      <div className="section-toggle" data-testid="education-section-toggle">
        {Object.keys(SECTION_META).map(s => (
          <button
            key={s}
            onClick={() => setActiveSection(s)}
            className={`section-tab${activeSection === s ? ' active' : ''}`}
            data-testid={`section-tab-${s.toLowerCase()}`}
          >
            {SECTION_META[s].label}
          </button>
        ))}
      </div>

      {/* Pillars */}
      <div className="calm-pillars">
        {meta.pillars.map(p => (
          <div key={p.title} className="calm-pillar">
            <i className={`fa-solid ${p.icon}`} />
            <div className="calm-pillar-title">{p.title}</div>
            <div className="calm-pillar-sub">{p.sub}</div>
          </div>
        ))}
      </div>

      {/* Category dropdown + search */}
      <div style={{ padding: '12px 16px 8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <select
          className="cat-dropdown"
          value={activeCategory}
          onChange={e => setActiveCategory(e.target.value)}
          data-testid="education-category-dropdown"
        >
          {categories.map(c => (
            <option key={c} value={c}>
              {c} {c !== 'All' ? `(${sectionArticles.filter(a => a.category === c).length})` : `(${sectionArticles.length})`}
            </option>
          ))}
        </select>
        <div style={{ position: 'relative' }}>
          <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', fontSize: '0.79rem' }} />
          <input
            className="input-field"
            placeholder="Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 32, fontSize: '0.93rem' }}
            data-testid="education-search"
          />
        </div>
      </div>

      {/* Featured row (Calm blog grid feel) */}
      {activeCategory === 'All' && !search && featured.length > 0 && (
        <div style={{ padding: '12px 0' }}>
          <div className="eyebrow" style={{ padding: '0 16px', marginBottom: 10 }}>Editor's Picks</div>
          <div className="scroll-row">
            {featured.map(a => (
              <motion.div key={a.id} className="course-card" whileHover={{ y: -2 }} onClick={() => openArticle(a)} data-testid={`featured-${a.id}`}>
                <div className="course-card-img" style={{ background: `linear-gradient(135deg, rgba(212,175,55,0.12), var(--bg2))` }}>
                  <i className={`fa-solid ${CAT_ICONS[a.category] || 'fa-book'}`} style={{ color: 'var(--gold)', fontSize: '2.5rem' }} />
                </div>
                <div className="course-card-body">
                  <div className="course-card-cat">{a.category}</div>
                  <div className="course-card-title">{a.title}</div>
                  <div className="course-card-meta">{a.read_time} · Dr. Vikram</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Articles list */}
      <div>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div className="gold-loader" style={{ margin: '0 auto' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.93rem' }}>
            No articles found in this category.
          </div>
        ) : (
          <>
            <div className="eyebrow" style={{ padding: '12px 16px 8px' }}>
              {activeCategory === 'All' ? 'All articles' : activeCategory} · {filtered.length}
            </div>
            {filtered.map((a, i) => (
              <motion.div key={a.id} className="article-list-item"
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.015 }}
                onClick={() => openArticle(a)}
                data-testid={`article-item-${a.id}`}>
                <div className="article-icon">
                  <i className={`fa-solid ${CAT_ICONS[a.category] || 'fa-book'}`} />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="article-title">{a.title}</div>
                  <div className="article-meta">{a.category} · {a.read_time}</div>
                </div>
                <i className="fa-solid fa-chevron-right" style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }} />
              </motion.div>
            ))}
          </>
        )}
      </div>

      {paywall && <Paywall trigger="This article requires sign-in." onClose={() => setPaywall(false)} />}
    </div>
  );
}
